import type { Ref } from "preact";
import { createPortal } from "preact/compat";
import { Icon, type IconName } from "../ui/icon";

const CHIP =
	"inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3.5 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-carmesim-600";
const CHIP_ON = "border-verde-600 bg-verde-600 text-white";
const CHIP_OFF = "border-line-strong bg-surface-raised text-ink hover:bg-surface-sunken";

const TONES = { primary: "odo-button__primary", secondary: "odo-button__secondary", alt: "odo-button__alt" };
const SIZES = { md: "min-h-11 px-3", sm: "min-h-9 px-3" };

// Every row of actions is centred, as the old sheet's is.
export const ACTION_ROW = "flex flex-wrap items-center justify-center gap-2";

// The site's button: `primary` red, `secondary` green, `alt` outlined, with an icon. `onClick` gets the button, for a
// popup that returns focus to it; a submit button leaves it out.
export function Button({
	tone,
	icon,
	label,
	title,
	labelClass,
	srOnly,
	size = "md",
	type = "button",
	disabled,
	busy,
	buttonRef,
	attrs,
	expanded,
	onClick,
}: {
	tone: keyof typeof TONES;
	icon: IconName;
	label: string;
	// A tooltip beyond the label.
	title?: string;
	labelClass?: string;
	srOnly?: string;
	size?: keyof typeof SIZES;
	type?: "button" | "submit";
	disabled?: boolean;
	// For a button whose work is under way.
	busy?: boolean;
	buttonRef?: Ref<HTMLButtonElement>;
	attrs?: Record<`data-${string}`, string>;
	// For a button that opens or widens something.
	expanded?: boolean;
	onClick?: (button: HTMLButtonElement) => void;
}) {
	return (
		<button
			type={type}
			ref={buttonRef}
			disabled={disabled}
			title={title}
			{...attrs}
			aria-expanded={expanded}
			aria-busy={busy}
			aria-label={labelClass ? label : undefined}
			class={`${TONES[tone]} ${SIZES[size]} disabled:pointer-events-none disabled:opacity-60`}
			onClick={onClick && ((event) => onClick(event.currentTarget))}
		>
			<Icon name={icon} class="odo-button__icon" />
			<span class={labelClass}>{label}</span>
			{srOnly && <span class="sr-only"> {srOnly}</span>}
		</button>
	);
}

// A link to a Centro de Ajuda article, in a new tab so the page and its draft stay where they are. Renders nothing without an href.
export function HelpLink({ href, label }: { href?: string; label?: string }) {
	if (!href || !label) return null;
	return (
		<a class="text-sm font-bold text-link underline hover:text-link-hover hover:no-underline" href={href} target="_blank" rel="noopener">
			{label}
		</a>
	);
}

// The quiet action beside a primary one; it takes the colour of what it sits on.
export function TextButton({
	label,
	onClick,
	buttonRef,
	disabled,
}: {
	label: string;
	onClick: () => void;
	buttonRef?: Ref<HTMLButtonElement>;
	disabled?: boolean;
}) {
	return (
		<button
			type="button"
			ref={buttonRef}
			disabled={disabled}
			class="cs-tap text-sm font-bold underline underline-offset-2 hover:no-underline disabled:cursor-not-allowed disabled:opacity-60"
			onClick={onClick}
		>
			{label}
		</button>
	);
}

// An on/off pill: green when on; `tall` is the 44px touch size.
export function Chip({ label, on, icon, tall = false, onToggle }: { label: string; on: boolean; icon?: IconName; tall?: boolean; onToggle: () => void }) {
	return (
		<button type="button" aria-pressed={on} class={`${CHIP} ${tall ? "min-h-11" : "min-h-10"} ${on ? CHIP_ON : CHIP_OFF}`} onClick={onToggle}>
			{icon && <Icon name={icon} class="h-3.5 w-3.5" />}
			{label}
		</button>
	);
}

// A portal, so it sits over the window and over an open popup, not inside the sheet's container.
export function Toast({ message }: { message: string }) {
	return createPortal(
		<div role="alert" class="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-sm rounded-lg bg-carmesim-600 px-3 py-2 text-sm font-bold text-white shadow-md">
			{message}
		</div>,
		document.body,
	);
}
